import React, { useRef, useState, useEffect } from "react";
import { X, Pen, Type, Upload, Trash2, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";

const TABS = [
  { id: "draw", label: "Dibujar", icon: Pen },
  { id: "type", label: "Escribir", icon: Type },
  { id: "upload", label: "Subir", icon: Upload },
];

const SIGNATURE_FONTS = [
  { id: "cursive1", name: "Cursiva clásica", style: { fontFamily: '"Dancing Script", "Brush Script MT", cursive', fontWeight: 600, fontSize: 44 } },
  { id: "cursive2", name: "Manuscrita", style: { fontFamily: '"Caveat", "Segoe Script", cursive', fontWeight: 500, fontSize: 44 } },
  { id: "italic", name: "Italica formal", style: { fontFamily: '"Instrument Serif", Georgia, serif', fontStyle: "italic", fontWeight: 500, fontSize: 38 } },
];

/**
 * Captures a signature in three modes: draw, type (rendered as image via canvas), upload.
 * onConfirm receives: { tipo, imagen_base64 (no prefix), nombre_escrito? }
 */
export default function SignatureModal({ open, onClose, defaultName = "", onConfirm }) {
  const [tab, setTab] = useState("draw");
  const [typedName, setTypedName] = useState(defaultName);
  const [fontId, setFontId] = useState(SIGNATURE_FONTS[0].id);
  const [uploadedFile, setUploadedFile] = useState(null); // { name, base64 (pure), mime }
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const lastPt = useRef(null);
  const [hasDrawn, setHasDrawn] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setTypedName(defaultName);
      setTab("draw");
      setHasDrawn(false);
      setUploadedFile(null);
    }
  }, [open, defaultName]);

  // -------- Canvas drawing --------
  const resizeCanvas = () => {
    const c = canvasRef.current;
    if (!c) return;
    const parent = c.parentElement;
    if (!parent) return;
    const ratio = window.devicePixelRatio || 1;
    const w = parent.clientWidth;
    const h = 180;
    c.width = w * ratio;
    c.height = h * ratio;
    c.style.width = `${w}px`;
    c.style.height = `${h}px`;
    const ctx = c.getContext("2d");
    ctx.scale(ratio, ratio);
    ctx.lineWidth = 2.2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#0A2540";
    // Fill white background
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, w, h);
  };

  useEffect(() => {
    if (open && tab === "draw") {
      setTimeout(resizeCanvas, 50);
      const onR = () => resizeCanvas();
      window.addEventListener("resize", onR);
      return () => window.removeEventListener("resize", onR);
    }
  }, [open, tab]);

  const getPt = (e) => {
    const c = canvasRef.current;
    const r = c.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - r.left;
    const y = (e.touches ? e.touches[0].clientY : e.clientY) - r.top;
    return { x, y };
  };
  const startDraw = (e) => {
    e.preventDefault();
    drawing.current = true;
    lastPt.current = getPt(e);
  };
  const moveDraw = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const c = canvasRef.current;
    const ctx = c.getContext("2d");
    const p = getPt(e);
    ctx.beginPath();
    ctx.moveTo(lastPt.current.x, lastPt.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    lastPt.current = p;
    setHasDrawn(true);
  };
  const endDraw = () => {
    drawing.current = false;
    lastPt.current = null;
  };
  const clearCanvas = () => {
    const c = canvasRef.current;
    const ctx = c.getContext("2d");
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, c.width, c.height);
    setHasDrawn(false);
  };

  // -------- Typed-to-image (canvas) --------
  const buildTypedSignaturePNG = () => {
    const off = document.createElement("canvas");
    const w = 800;
    const h = 240;
    off.width = w;
    off.height = h;
    const ctx = off.getContext("2d");
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, w, h);
    const f = SIGNATURE_FONTS.find((x) => x.id === fontId) || SIGNATURE_FONTS[0];
    const style = f.style;
    const fontFamily = style.fontFamily.split(",")[0].replace(/"/g, "");
    const fontWeight = style.fontWeight || 500;
    const fontStyle = style.fontStyle || "normal";
    const fontSize = 96;
    ctx.font = `${fontStyle} ${fontWeight} ${fontSize}px ${fontFamily}, cursive`;
    ctx.fillStyle = "#0A2540";
    ctx.textBaseline = "middle";
    ctx.textAlign = "center";
    ctx.fillText(typedName || " ", w / 2, h / 2);
    return off.toDataURL("image/png");
  };

  // -------- Upload --------
  const onFileChange = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!["image/png", "image/jpeg", "image/jpg"].includes(f.type)) {
      toast.error("Solo PNG o JPG");
      return;
    }
    if (f.size > 4 * 1024 * 1024) {
      toast.error("Máximo 4 MB");
      return;
    }
    const dataUrl = await new Promise((res, rej) => {
      const r = new FileReader();
      r.onload = () => res(r.result);
      r.onerror = rej;
      r.readAsDataURL(f);
    });
    const pure = String(dataUrl).split(",")[1] || "";
    setUploadedFile({ name: f.name, base64: pure, mime: f.type, dataUrl });
  };

  const stripPrefix = (dataUrl) => String(dataUrl).split(",")[1] || "";

  const canConfirm =
    (tab === "draw" && hasDrawn) ||
    (tab === "type" && typedName.trim().length > 1) ||
    (tab === "upload" && uploadedFile);

  const confirm = () => {
    if (busy) return;
    setBusy(true);
    try {
      let payload;
      if (tab === "draw") {
        const dataUrl = canvasRef.current.toDataURL("image/png");
        payload = { tipo: "draw", imagen_base64: stripPrefix(dataUrl) };
      } else if (tab === "type") {
        const dataUrl = buildTypedSignaturePNG();
        payload = { tipo: "type", imagen_base64: stripPrefix(dataUrl), nombre_escrito: typedName.trim() };
      } else {
        payload = { tipo: "upload", imagen_base64: uploadedFile.base64 };
      }
      onConfirm?.(payload);
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid="signature-modal">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose}></div>
      <div className="relative bg-white rounded-2xl w-full max-w-lg overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b" style={{ borderColor: "var(--border-subtle)" }}>
          <div>
            <h3 className="text-xl font-extrabold tracking-tight" style={{ fontFamily: "Manrope" }}>
              Firma tu carta de asignación
            </h3>
            <p className="text-[12px] mt-0.5" style={{ color: "var(--text-muted)" }}>
              Dibuja, escribe tu nombre o sube una imagen de tu firma.
            </p>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-md flex items-center justify-center hover:bg-[var(--bg-soft)]" data-testid="signature-close">
            <X size={16}/>
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b" style={{ borderColor: "var(--border-subtle)" }}>
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`flex-1 inline-flex items-center justify-center gap-2 py-3 text-[13px] font-semibold transition ${active ? "" : "opacity-60 hover:opacity-100"}`}
                style={{
                  borderBottom: active ? "2px solid var(--brand-accent)" : "2px solid transparent",
                  color: active ? "var(--text-primary)" : "var(--text-muted)",
                }}
                data-testid={`signature-tab-${t.id}`}
              >
                <Icon size={14}/> {t.label}
              </button>
            );
          })}
        </div>

        {/* Content */}
        <div className="p-5">
          {tab === "draw" && (
            <div>
              <div className="rounded-lg border bg-white overflow-hidden" style={{ borderColor: "var(--border-medium)" }}>
                <canvas
                  ref={canvasRef}
                  onMouseDown={startDraw}
                  onMouseMove={moveDraw}
                  onMouseUp={endDraw}
                  onMouseLeave={endDraw}
                  onTouchStart={startDraw}
                  onTouchMove={moveDraw}
                  onTouchEnd={endDraw}
                  className="w-full block cursor-crosshair touch-none"
                  data-testid="signature-canvas"
                />
              </div>
              <div className="flex justify-between mt-3">
                <button onClick={clearCanvas} className="inline-flex items-center gap-1.5 text-[12px] font-semibold opacity-70 hover:opacity-100" data-testid="signature-clear">
                  <Trash2 size={13}/> Borrar
                </button>
                <span className="text-[11.5px]" style={{ color: "var(--text-muted)" }}>Dibuja con tu dedo o mouse</span>
              </div>
            </div>
          )}

          {tab === "type" && (
            <div>
              <input
                type="text"
                value={typedName}
                onChange={(e) => setTypedName(e.target.value)}
                placeholder="Tu nombre completo o iniciales"
                className="field-input field-input--compact w-full"
                data-testid="signature-type-input"
              />
              <div className="mt-3 flex gap-2">
                {SIGNATURE_FONTS.map((f) => {
                  const active = fontId === f.id;
                  return (
                    <button
                      key={f.id}
                      onClick={() => setFontId(f.id)}
                      className={`flex-1 rounded-lg border px-2 py-2 transition ${active ? "" : "opacity-70 hover:opacity-100"}`}
                      style={{
                        borderColor: active ? "var(--brand-accent)" : "var(--border-medium)",
                        background: active ? "rgba(238,81,71,0.04)" : "white",
                      }}
                      data-testid={`signature-font-${f.id}`}
                    >
                      <div className="overflow-hidden h-14 flex items-center justify-center" style={f.style}>
                        {typedName || "Nombre"}
                      </div>
                      <div className="text-[10px] mt-1 uppercase tracking-[0.1em] font-bold" style={{ color: "var(--text-muted)" }}>{f.name}</div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {tab === "upload" && (
            <div>
              {!uploadedFile ? (
                <label className="flex flex-col items-center justify-center gap-2 px-4 py-10 rounded-lg border cursor-pointer transition hover:bg-[var(--bg-soft)]" style={{ borderColor: "var(--border-medium)", borderStyle: "dashed" }}>
                  <Upload size={22} style={{ color: "var(--brand-accent)" }}/>
                  <span className="text-[14px] font-semibold">Subir imagen</span>
                  <span className="text-[12px]" style={{ color: "var(--text-muted)" }}>PNG o JPG (máx. 4MB)</span>
                  <input type="file" accept="image/png,image/jpeg" onChange={onFileChange} className="hidden" data-testid="signature-upload-input"/>
                </label>
              ) : (
                <div>
                  <div className="rounded-lg border p-3 flex items-center justify-center bg-white" style={{ borderColor: "var(--border-medium)", minHeight: 140 }}>
                    <img src={uploadedFile.dataUrl} alt="firma" className="max-h-32 object-contain" data-testid="signature-upload-preview"/>
                  </div>
                  <button onClick={() => setUploadedFile(null)} className="mt-3 text-[12px] font-semibold opacity-70 hover:opacity-100 inline-flex items-center gap-1.5">
                    <Trash2 size={13}/> Quitar y subir otra
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 p-5 border-t" style={{ borderColor: "var(--border-subtle)", background: "var(--bg-soft)" }}>
          <p className="text-[11px]" style={{ color: "var(--text-soft)" }}>
            Tu firma electrónica simple queda registrada con fecha y hora.
          </p>
          <button
            onClick={confirm}
            disabled={!canConfirm || busy}
            className="btn-accent"
            style={{ padding: "0.65rem 1.2rem", fontSize: 13.5 }}
            data-testid="signature-confirm"
          >
            Firmar y descargar <ArrowUpRight size={14}/>
          </button>
        </div>
      </div>
    </div>
  );
}
